import { type FC, type MouseEventHandler, type PropsWithChildren, useCallback } from 'react';

import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import ImageComponent from '@vakantiesnl/image-component';
import { useGetAccommodationTopBannerData } from '@vakantiesnl/services/src/contentful/queries/accommodationTopBannerImage';

import { useStyles } from './AccommodationTopBannerImage.styles';

const ConditionalLinkComponent: FC<
	PropsWithChildren<{ href: string | null; onClick: MouseEventHandler<HTMLAnchorElement> }>
> = ({ children, href, onClick }) => {
	const { classes } = useStyles();

	return href ? (
		<Link href={href} target="_blank" onClick={onClick} prefetch={false} className={classes.link}>
			{children}
		</Link>
	) : (
		<>{children}</>
	);
};

type AccommodationTopBannerImageProps = {
	entityId: string;
	name: string;
};

export const AccommodationTopBannerImage: FC<AccommodationTopBannerImageProps> = ({ entityId, name }) => {
	const { classes, cx } = useStyles();
	const { isTabletOrIsDesktop } = useBreakpoints();
	const { accommodationTopBannerData, isLoadingCountryData } = useGetAccommodationTopBannerData(entityId);

	const handleOnLinkClick = useCallback(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ trackAccommodationTopBannerClick }) =>
				track(trackAccommodationTopBannerClick(entityId)),
			),
		);
	}, [entityId]);

	if (isLoadingCountryData) return null;

	if (accommodationTopBannerData) {
		return (
			<div
				className={cx(classes.imageWrapper, {
					[classes.fullWidthImage]:
						/** Max desktop image size is 1200px and mobile 728px. */
						(isTabletOrIsDesktop && accommodationTopBannerData.largeBanner.width === 1200) ||
						(!isTabletOrIsDesktop && accommodationTopBannerData.smallBanner.width === 728),
				})}
				data-cy="accommodation-top-banner"
			>
				<ConditionalLinkComponent onClick={handleOnLinkClick} href={accommodationTopBannerData.url}>
					<ImageComponent
						alt={`${name} Top Banner Image`}
						useLoader="Contentful"
						src={
							isTabletOrIsDesktop
								? accommodationTopBannerData.largeBanner.url
								: accommodationTopBannerData.smallBanner.url
						}
						width={
							isTabletOrIsDesktop
								? accommodationTopBannerData.largeBanner.width
								: accommodationTopBannerData.smallBanner.width
						}
						height={
							isTabletOrIsDesktop
								? accommodationTopBannerData.largeBanner.height
								: accommodationTopBannerData.smallBanner.height
						}
						quality={75}
						className={classes.image}
					/>
				</ConditionalLinkComponent>
			</div>
		);
	}
};
